/**
 * La forma de los datos que devuelve el backend.
 *
 * UNA ACLARACION QUE EVITA UN BUG CARO
 * ------------------------------------
 * Postgres serializa `numeric` como STRING, no como number. `"1650000.00"` no es
 * `1650000`, y `"1200.00" + 100` da `"1200.00100"`. Por eso todos los importes se
 * tipan `Numerico = number | string`: el tipo obliga a pasar por `aNumero()`
 * antes de hacer cuentas, en vez de dejar que una concatenacion silenciosa
 * aparezca en un total.
 *
 * Los campos que la base define como `int` (ids, contadores) si llegan como
 * number y se tipan `number`.
 */
import type { Moneda } from "@tora/shared";

/** Un importe tal como viaja: `numeric` de Postgres llega como string. */
export type Numerico = number | string;

/** Convierte lo que venga a number. `null`, `""` y basura dan 0. */
export function aNumero(valor: Numerico | null | undefined): number {
  if (valor === null || valor === undefined || valor === "") return 0;
  const n = typeof valor === "number" ? valor : Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/** Listado paginado. Es la forma que devuelve `consultar()` del backend. */
export interface Pagina<T> {
  filas: T[];
  total: number;
  limite?: number;
  offset?: number;
}

export interface Paginacion {
  limite?: number;
  offset?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sesion
// ─────────────────────────────────────────────────────────────────────────────

export type Rol = "admin" | "operador";

export interface Usuario {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
}

/** `GET /sesion/estado`. `false` manda a la pantalla de primer uso. */
export interface EstadoInstalacion {
  instalado: boolean;
}

export interface RespuestaSesion {
  usuario: Usuario;
  token?: string;
}

export interface CredencialesLogin {
  email: string;
  password: string;
}

export interface DatosInstalacion {
  nombre: string;
  email: string;
  password: string;
}

/** `GET /usuarios`: una cuenta de la lista de Configuracion. Todas son admin. */
export interface FilaUsuario {
  id: string;
  nombre: string;
  email: string;
  activo: boolean;
  created_at: string;
}

/** `POST /usuarios`. El rol no se elige: siempre administrador. */
export interface NuevoUsuario {
  nombre: string;
  email: string;
  password: string;
}

/** `PATCH /usuarios/:id`. Lo que no viene no se toca. */
export interface CambiosUsuario {
  nombre?: string;
  password?: string;
  activo?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Precios — la consulta del dia
// ─────────────────────────────────────────────────────────────────────────────

export type Disponibilidad = "DISPONIBLE" | "SIN_STOCK" | "A_PEDIDO" | string;

/** Una fila de `buscar_precio`. Contesta "cuanto sale" y "si hay" de una. */
export interface ResultadoPrecio {
  producto_id: number;
  codigo_interno: string;
  nombre: string;
  rubro: string | null;
  precio: Numerico;
  precio_moneda: Moneda;
  precio_ars: Numerico;
  precio_usd: Numerico;
  stock_actual: Numerico;
  disponibilidad: Disponibilidad;
  costo_ars: Numerico;
  margen_ars: Numerico;
  /** Ya formateado para pegar en el chat. No se rearma en el frontend. */
  texto_whatsapp: string;
  relevancia: number;
}

export interface RespuestaPrecios {
  q: string;
  resultados: ResultadoPrecio[];
  total: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Catalogo
// ─────────────────────────────────────────────────────────────────────────────

export type MarkupTipo = "PORCENTAJE" | "FIJO";
export type Redondeo = "NINGUNO" | "UNIDAD" | "CIEN" | "QUINIENTOS" | "MIL" | "TERMINACION_990";

export interface Producto {
  id: number;
  codigo_interno: string;
  codigo_externo: string | null;
  nombre: string;
  descripcion: string | null;
  /** QUE es la cosa: la familia. */
  rubro_id: number | null;
  /** DE QUIEN es. Ortogonal a la familia; null mientras nadie la haya cargado. */
  marca: string | null;
  proveedor_id: number | null;
  costo: Numerico;
  costo_moneda: Moneda;
  costo_actualizado_at: string | null;
  markup_tipo: MarkupTipo;
  markup_valor: Numerico;
  markup_moneda: Moneda | null;
  precio_moneda: Moneda;
  precio_manual: Numerico | null;
  precio_venta: Numerico;
  redondeo: Redondeo;
  stock_actual: Numerico;
  stock_minimo: Numerico;
  stock_ideal: Numerico;
  publicado: boolean;
  imagen_url: string | null;
  a_pedido: boolean;
  activo: boolean;
  /** Cuándo pasó por el relevamiento físico. `null` = todavía no se contó. */
  relevado_at: string | null;
  relevado_por: string | null;
  // ── La ficha del usado (0023): una unidad usada es un producto con stock 1 ──
  es_usado: boolean;
  imei: string | null;
  bateria_pct: number | null;
  color: string | null;
  memoria_gb: number | null;
  caja_cable: boolean | null;
  garantia_meses: number | null;
}

export interface FiltrosProductos extends Paginacion {
  q?: string;
  rubro_id?: number;
  marca?: string;
  proveedor_id?: number;
  /** «todos» suma los dados de baja (la compra los muestra marcados). */
  activo?: boolean | "todos";
  publicado?: boolean;
  es_usado?: boolean;
}

/**
 * Alta de producto.
 *
 * `precio_venta` NO va: lo calcula el trigger a partir de costo, markup y
 * cotizacion. Mandarlo seria pisar el calculo con un numero que envejece.
 * `precio_moneda` si es obligatorio: la tabla tiene default 'ARS' y ese default
 * silencioso deja un iPhone cotizado en pesos que nunca se repricia.
 */
export interface NuevoProducto {
  nombre: string;
  precio_moneda: Moneda;
  codigo_interno?: string;
  codigo_externo?: string | null;
  descripcion?: string | null;
  rubro_id?: number | null;
  marca?: string | null;
  proveedor_id?: number | null;
  costo?: number;
  costo_moneda?: Moneda;
  markup_tipo?: MarkupTipo;
  markup_valor?: number;
  markup_moneda?: Moneda | null;
  precio_manual?: number | null;
  redondeo?: Redondeo;
  stock_minimo?: number;
  stock_ideal?: number;
  publicado?: boolean;
  imagen_url?: string | null;
  orden?: number;
  a_pedido?: boolean;
  activo?: boolean;
  es_usado?: boolean;
  imei?: string | null;
  bateria_pct?: number | null;
  color?: string | null;
  memoria_gb?: number | null;
  caja_cable?: boolean | null;
  garantia_meses?: number | null;
}

export type CambiosProducto = Partial<NuevoProducto>;

export interface Rubro {
  id: number;
  nombre: string;
  slug: string;
  orden: number;
  activo: boolean;
  /** Si el rubro entero se ve en el catalogo publico. */
  publicado: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tienda online: lo que necesita la pantalla que decide que se publica
// ─────────────────────────────────────────────────────────────────────────────

/** Un rubro con sus cuatro numeros. Sale de `v_publicacion_rubros`. */
export interface RubroPublicacion {
  id: number;
  nombre: string;
  slug: string;
  orden: number;
  publicado: boolean;
  /** Productos activos del rubro. */
  total: number;
  publicados: number;
  /** De los publicados, cuantos tienen foto. */
  con_foto: number;
  /** Publicados sin precio: el catalogo no los muestra hasta que lo tengan. */
  sin_precio: number;
}

export interface ResumenPublicacion {
  rubros: RubroPublicacion[];
  totales: {
    publicados: number;
    rubros_visibles: number;
    /** Visibles y con algo publicado: los que el cliente ve en el menu. */
    rubros_con_publicados?: number;
    sin_foto: number;
    sin_precio: number;
  };
  negocio: {
    whatsapp: string | null;
    instagram: string | null;
    logo_url: string | null;
  };
  /** El link que va al Linktree. */
  url_publica: string;
}

/**
 * Una oferta del catalogo publico.
 *
 * No es un producto: no tiene stock, no entra al carrito y su precio es el de
 * esta fila (migracion 0060). Se carga a mano desde Tienda online.
 */
/**
 * Los siete tipos de oferta (0100). Los tres primeros son "este equipo a este
 * precio"; EFECTIVO es un porcentaje; PREVENTA puede tener precio; CANJE y
 * ENVIO no tienen: se consultan.
 */
export type ClaseOferta =
  "COMBO" | "BAJA" | "ULTIMAS" | "EFECTIVO" | "PREVENTA" | "CANJE" | "ENVIO";

export interface Oferta {
  id: number;
  clase: ClaseOferta;
  titulo: string;
  detalle: string | null;
  /** null en los tipos que no lo llevan (canje, envio) o no lo cargaron. */
  precio: string | null;
  moneda: Moneda;
  /** Lo que costaba antes, o costaria por separado. De aca sale el ahorro. */
  precio_antes: string | null;
  producto_id: number | null;
  activa: boolean;
  orden: number;
  /** El descuento de una oferta EFECTIVO, en %. */
  porcentaje: number | null;
  /** La foto propia de la oferta (0110), con `?v=`. Null: usa la del equipo. */
  imagen_url: string | null;
}

/** Lo que se manda al crear o editar una oferta. */
export interface OfertaEnvio {
  clase: ClaseOferta;
  titulo: string;
  detalle: string | null;
  precio: number | null;
  moneda: Moneda;
  precio_antes: number | null;
  /** De que equipo habla. No manda el precio: sirve para que el mensaje lo nombre. */
  producto_id: number | null;
  activa: boolean;
  orden: number;
  porcentaje?: number | null;
}

export interface Proveedor {
  id: number;
  nombre: string;
  moneda_lista: Moneda;
  activo: boolean;
  codigo: string | null;
  contacto: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  localidad: string | null;
  observaciones: string | null;
  /** Dias habiles entre el pedido y la entrega; Reposicion lo usa (0140). */
  demora_dias_habiles?: number;
}

/** Lo que se puede tocar de un proveedor ya creado. */
export type CambiosProveedor = Partial<Omit<Proveedor, "id">>;

/**
 * Una fila de `v_saldos_proveedores`, que es lo que devuelve GET /proveedores.
 *
 * Extiende `Proveedor` y no lo reemplaza: la vista es un superset de la tabla a
 * proposito, para que la pantalla de ABM y los desplegables sigan andando sin
 * una segunda vuelta por el saldo.
 *
 * LAS DOS MONEDAS VAN SEPARADAS. `saldo_ars_hoy` existe para ordenar y
 * totalizar, y por eso dice "hoy": es una foto a la cotizacion del momento, no
 * una deuda que alguien haya pactado en pesos.
 */
export interface SaldoProveedor extends Proveedor {
  proveedor_id: number;
  saldo_ars: Numerico;
  saldo_usd: Numerico;
  saldo_ars_hoy: Numerico;
  movimientos: Numerico;
  ultimo_pago: string | null;
  ultimo_movimiento: string | null;
  deuda_mas_vieja: string | null;
  /** Edad de la deuda pendiente mas vieja. Null si no se debe nada. */
  antiguedad_dias: Numerico | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stock
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoStock = "OK" | "BAJO" | "CRITICO" | "SIN_STOCK" | "NEGATIVO";

export interface FilaStock {
  id: number;
  codigo_interno: string;
  codigo_externo: string | null;
  nombre: string;
  rubro: string | null;
  proveedor: string | null;
  stock_actual: Numerico;
  stock_minimo: Numerico;
  stock_ideal: Numerico;
  estado: EstadoStock;
  costo: Numerico;
  costo_moneda: Moneda;
  precio_venta: Numerico;
  precio_moneda: Moneda;
  valor_costo_ars: Numerico;
  valor_costo_usd: Numerico;
  valor_venta_ars: Numerico;
  valor_venta_usd: Numerico;
  reponer: Numerico;
  /**
   * La ficha del equipo, para armar el nombre al dibujarlo (0073). El color, la
   * memoria y la bateria salieron de adentro del nombre y sin esto dos unidades
   * del mismo modelo se leen igual. Ver `catalogo/nombre.ts`.
   */
  es_usado: boolean;
  color: string | null;
  memoria_gb: number | null;
  bateria_pct: number | null;
}

export type OrdenStock =
  | "nombre"
  | "codigo_interno"
  | "rubro"
  | "proveedor"
  | "stock_actual"
  | "estado"
  | "valor_costo_ars"
  | "valor_costo_usd"
  | "valor_venta_ars"
  | "valor_venta_usd"
  | "reponer";

export interface FiltrosStock extends Paginacion {
  q?: string;
  /** Una familia, o varias separadas por coma: una seccion del mostrador son varias. */
  rubro?: string;
  proveedor?: string;
  estado?: EstadoStock;
  /** Las tres secciones que no se pueden pedir por familia. */
  es_usado?: boolean;
  es_iphone_sellado?: boolean;
  /**
   * La seccion «Usados», que es la de los iPHONE usados.
   *
   * `es_usado` solo no alcanza: un Samsung usado es `es_usado` y sin embargo se
   * busca en «Otras marcas», con los celulares (ver `secciones.ts`).
   */
  es_usado_iphone?: boolean;
  a_pedido?: boolean;
  /**
   * Solo lo que hay en el local: deja afuera los productos en cero.
   *
   * Es lo que mira Inventario. El catalogo entero —con lo que esta en cero, que
   * sigue siendo un producto que se vende— vive en Catalogo.
   */
  con_stock?: boolean;
  orden?: OrdenStock;
  desc?: boolean;
}

/**
 * Una fila de `v_stock_por_familia`: el inventario sumado por familia.
 *
 * Viene partido tambien por `es_iphone_sellado` y `es_usado_iphone` para que la
 * pantalla pueda sumar las familias en secciones del mostrador con el mismo
 * mapa que usa para filtrar (`secciones.ts`). La base no sabe de secciones.
 *
 * `es_usado` sigue viniendo porque es un dato de la mercaderia —cuanta plata
 * hay en equipos de segunda mano— pero ya no decide la seccion: eso lo hace
 * `es_usado_iphone`, porque «Usados» es la solapa de los iPhone usados.
 *
 * `productos` cuenta los que TIENEN unidades, que es lo que lista la tabla de
 * Stock (0081). Las unidades y la plata son las de los que tienen unidades: un
 * negativo no resta (0121_5), va aparte en `sin_stock_*`.
 */
export interface StockPorFamilia {
  rubro: string;
  es_usado: boolean;
  a_pedido: boolean;
  es_iphone_sellado: boolean;
  es_usado_iphone: boolean;
  productos: number | string;
  unidades: number | string;
  valor_costo_ars: number | string;
  valor_costo_usd: number | string;
  valor_venta_ars: number | string;
  valor_venta_usd: number | string;
  /**
   * Lo vendido sin stock, en positivo (0121_5): los productos en negativo, las
   * unidades que faltan y su costo. `productos`, `unidades` y los valores de
   * arriba cuentan solo lo que esta en el local. Opcional: una base sin la
   * migracion no las trae, y cuentan cero.
   */
  sin_stock_productos?: number | string;
  sin_stock_unidades?: number | string;
  sin_stock_costo_ars?: number | string;
  sin_stock_costo_usd?: number | string;
}

export type SentidoMovimiento = "INGRESO" | "SALIDA";

/** Una fila de `v_movimientos_stock`: un ingreso o una salida, con lo que quedo despues. */
export interface MovimientoStock {
  /** `INGRESO-12` o `SALIDA-12`: unica entre las dos tablas. */
  clave: string;
  sentido: SentidoMovimiento;
  movimiento_id: number;
  fecha: string;
  producto_id: number;
  codigo_interno: string;
  producto: string;
  /** Siempre positiva. El signo va en `delta`. */
  cantidad: Numerico;
  delta: Numerico;
  tipo: string;
  referencia: string | null;
  observaciones: string | null;
  costo_unitario: Numerico | null;
  costo_moneda: Moneda | null;
  /** Nombre del perfil que lo cargo. Vacio si no hay perfil. */
  usuario: string | null;
  /** En un retiro de mercaderia: de que socio es. Lo agrega el backend. */
  socio?: string | null;
  /** El acumulado del producto despues de este movimiento. */
  stock_despues: Numerico;
  /**
   * La ficha del equipo, para armar el nombre al dibujarlo (0073). El color, la
   * memoria y la bateria salieron de adentro del nombre y sin esto dos unidades
   * del mismo modelo se leen igual. Ver `catalogo/nombre.ts`.
   */
  es_usado: boolean;
  color: string | null;
  memoria_gb: number | null;
  bateria_pct: number | null;
}

export interface FiltrosMovimientos extends Paginacion {
  producto_id?: number;
  sentido?: SentidoMovimiento;
  tipo?: string;
  q?: string;
}

export interface PaginaMovimientos extends Pagina<MovimientoStock> {
  /** El `stock_actual` del producto, solo cuando se filtro por `producto_id`. */
  stock_actual: Numerico | null;
}

export interface FilaReposicion {
  proveedor_id: number | null;
  proveedor: string;
  moneda_lista: Moneda | null;
  producto_id: number;
  codigo_interno: string;
  codigo_externo: string | null;
  nombre: string;
  stock_actual: Numerico;
  stock_minimo: Numerico;
  stock_ideal: Numerico;
  estado: EstadoStock;
  cantidad_sugerida: Numerico;
  costo: Numerico;
  costo_moneda: Moneda;
  costo_compra_ars: Numerico;
  costo_compra_usd: Numerico;
  /**
   * La ficha del equipo, para armar el nombre al dibujarlo (0073). El color, la
   * memoria y la bateria salieron de adentro del nombre y sin esto dos unidades
   * del mismo modelo se leen igual. Ver `catalogo/nombre.ts`.
   */
  es_usado: boolean;
  color: string | null;
  memoria_gb: number | null;
  bateria_pct: number | null;
  /** Lo vendido en los ultimos 30 dias, sin contar lo anulado (0098). */
  vendidas_30d: Numerico;
  /**
   * Por que entra (0140): por la rotacion, porque esta debajo del ideal
   * cargado, o por lo vendido cuando todavia no hay dos semanas de historia.
   */
  motivo: MotivoReposicion;
  /** El costo de UNA unidad en cada moneda, para recalcular al cambiar la cantidad. */
  costo_ars: Numerico;
  costo_usd: Numerico;
  proveedor_telefono: string | null;
  /** Lo vendido desde que se cargo la ultima compra (0102). */
  sin_reponer: Numerico;
  /** `nivel === "URGENTE"`: se agota en menos de 5 dias habiles, o de la demora si es mayor. */
  urgente: boolean;
  /** Cuando se cargo la ultima compra. `null` = nunca se compro. */
  ultima_compra: string | null;
  /** El pedido por WhatsApp que sigue esperando la compra, si hay. */
  pedido_at: string | null;
  pedido_cantidad: Numerico | null;
  /** Unidades por dia en los ultimos 90, contando solo los dias con stock (0140). */
  ritmo_diario: Numerico;
  /** Para cuantos dias alcanza el stock a ese ritmo. `null` = no se vende. */
  dias_stock: Numerico | null;
  rotacion: Rotacion;
  /** `null` = no hay que pedir, solo espera un pedido abierto. */
  nivel: NivelReposicion | null;
  /** Lo que tarda el proveedor, en dias corridos. */
  demora_dias: number;
  /** Si ya hay 14 dias de historia; sin eso la regla es lo vendido. */
  con_historia: boolean;
}

export type MotivoReposicion = "ROTACION" | "IDEAL" | "SIN_HISTORIA";
export type Rotacion = "ALTA" | "MEDIA" | "BAJA" | "SIN_VENTAS";
export type NivelReposicion = "URGENTE" | "PEDIR" | "AGOTADO";

/** Lo que se le pide a un proveedor desde Reposicion (0102). */
export interface PedidoReposicion {
  proveedor_id: number | null;
  items: Array<{ producto_id: number; cantidad: number }>;
}

export interface IngresoStock {
  producto_id: number;
  cantidad: number;
  costo_unitario?: number;
  /** Obligatoria si va `costo_unitario`. Un costo sin moneda no significa nada. */
  costo_moneda?: Moneda;
  tipo?: "COMPRA" | "AJUSTE" | "DEVOLUCION" | "SALDO_INICIAL" | "OTRO";
  referencia?: string;
  proveedor_id?: number;
  actualiza_costo?: boolean;
  fecha?: string;
  observaciones?: string;
}

export interface SalidaStock {
  producto_id: number;
  cantidad: number;
  /** VENTA no esta: esa salida la genera `emitir_venta` con el comprobante. */
  tipo?: "AJUSTE" | "MERMA" | "GARANTIA" | "REPARACION" | "RETIRO_SOCIO" | "OTRO";
  /** Solo con RETIRO_SOCIO. El costo no se manda: lo congela la base. */
  socio_id?: number;
  referencia?: string;
  fecha?: string;
  observaciones?: string;
}

export interface ResultadoMovimientoStock {
  ingreso_id?: number;
  salida_id?: number;
  producto_id: number;
  stock_actual: Numerico;
  costo?: Numerico;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ventas
// ─────────────────────────────────────────────────────────────────────────────

export type TipoComprobante = "VENTA" | "NC" | "ND";
export type EstadoComprobante = "EMITIDO" | "ANULADO";
export type DescuentoTipo = "PORCENTAJE" | "MONTO";

export interface Comprobante {
  id: number;
  tipo: TipoComprobante;
  numero: string;
  fecha: string;
  cliente_id: number | null;
  moneda: Moneda;
  cotizacion: Numerico;
  subtotal: Numerico;
  descuento: Numerico;
  total: Numerico;
  total_ars: Numerico;
  total_usd: Numerico;
  estado: EstadoComprobante;
  observaciones: string | null;
  /**
   * Parte secreta de la URL publica del remito (migracion 0021). Es `null` solo
   * en respuestas viejas cacheadas: en la base la columna es `not null`.
   */
  token_publico?: string | null;
}

export interface DetalleComprobante {
  id: number;
  comprobante_id: number;
  orden: number;
  tipo_item: "PRODUCTO" | "SERVICIO";
  producto_id: number | null;
  descripcion: string;
  cantidad: Numerico;
  precio_unitario: Numerico;
  descuento_pct: Numerico;
  importe: Numerico;
  serie_imei: string | null;
  /**
   * Lo que ya volvio por notas de credito (GET /ventas/:id). Solo viene en el
   * detalle de una venta; en el resto de los detalles no esta.
   */
  cantidad_acreditada?: Numerico;
}

/**
 * Un ingreso de stock que llego por una venta: el equipo que el cliente entrego
 * en parte de pago.
 *
 * Es la tabla `ingreso_stock` con el nombre del producto pegado, porque sin eso
 * la fila del panel seria un id. `comprobante_id` lo agrega la migracion 0051:
 * antes el unico vinculo con la venta era el texto de `referencia`.
 */
export interface EntregaDeVenta {
  /** `bigserial`: Postgres lo serializa como string, igual que los `numeric`. */
  id: Numerico;
  fecha: string;
  producto_id: number;
  cantidad: Numerico;
  costo_unitario: Numerico | null;
  costo_moneda: Moneda | null;
  cotizacion: Numerico | null;
  tipo: string;
  referencia: string | null;
  proveedor_id: number | null;
  actualiza_costo: boolean;
  observaciones: string | null;
  comprobante_id: number | null;
  created_at: string;
  /** Del join con `productos`. */
  producto_nombre: string;
  producto_imei: string | null;
  es_usado: boolean;
}

/**
 * `GET /ventas/:id`. El comprobante con todo lo que hizo falta para cobrarlo.
 *
 * `pagos` y `entregas` son las dos mitades de "como se cobro" y ninguna alcanza
 * sola: un equipo en parte de pago no toca caja, asi que una venta canjeada al
 * 100% tiene `pagos` vacio y esta cobrada igual.
 */
export interface ComprobanteConDetalle {
  comprobante: Comprobante;
  detalle: DetalleComprobante[];
  /**
   * La plata. Incluye los EGRESO de concepto DEVOLUCION que deja una anulacion
   * o una nota de credito: son movimientos de esta venta.
   */
  pagos: MovimientoCaja[];
  /** Los equipos recibidos en parte de pago. Casi siempre cero o uno. */
  entregas: EntregaDeVenta[];
}

export interface FiltrosVentas extends Paginacion {
  tipo?: TipoComprobante;
  cliente_id?: number;
  estado?: EstadoComprobante;
  desde?: string;
  hasta?: string;
  /**
   * Busca por numero de comprobante, en la base. Antes se filtraba en el
   * cliente sobre lo ya cargado y un comprobante viejo daba "no encontre"
   * existiendo.
   */
  q?: string;
}

/** Importe y cantidad de comprobantes de UNA moneda de facturacion. */
export interface TotalMoneda {
  importe: Numerico;
  cantidad: number;
}

/**
 * Los totales que devuelve `GET /ventas`, calculados sobre el set filtrado
 * COMPLETO y no sobre la pagina.
 *
 * No hay ningun campo que sume las dos monedas y no es un olvido: un
 * comprobante en dolares y uno en pesos no se suman sin decir a que cotizacion,
 * y cada uno tiene la suya congelada. El consolidado en pesos vive en Resultado
 * del negocio, que es donde se puede explicar.
 *
 * Reglas ya aplicadas por el backend: los ANULADO no cuentan (ni en el importe
 * ni en la cantidad) y las NC restan.
 */
export interface TotalesVentas {
  ars: TotalMoneda;
  usd: TotalMoneda;
  /** Cuantos quedaron afuera de los importes por estar anulados. */
  anulados: number;
}

/** `GET /ventas`: la pagina de comprobantes mas los totales del set entero. */
export interface PaginaVentas extends Pagina<Comprobante> {
  totales: TotalesVentas;
}

export interface ItemVenta {
  tipo_item?: "PRODUCTO" | "SERVICIO";
  producto_id?: number;
  descripcion?: string;
  cantidad: number;
  precio_unitario?: number;
  descuento_pct?: number;
  serie_imei?: string;
  observaciones?: string;
}

export interface NuevaVenta {
  /** Sin cliente es venta de mostrador al contado. */
  cliente_id?: number;
  /** Nunca tiene default. En un sistema bimonetario no hay default seguro. */
  moneda: Moneda;
  cotizacion?: number;
  fecha?: string;
  descuento_tipo?: DescuentoTipo;
  descuento_valor?: number;
  observaciones?: string;
  /** Confirma una venta que excede el limite de credito del cliente. */
  forzar?: boolean;
  items: ItemVenta[];
}

/** Lo que devuelve `emitir_venta`. Los dos ultimos campos no son decorativos. */
export interface ResultadoVenta {
  comprobante_id: number;
  numero: string;
  total: Numerico;
  moneda: Moneda;
  cliente_id: number | null;
  /** Que se vendio sin tener en stock. La venta se hizo igual: hay que verlo. */
  faltantes?: Array<{ producto_id: number; nombre?: string; faltante: Numerico }>;
  /** Cuanto credito le queda al cliente despues de esta venta. */
  credito_restante?: Numerico | null;
}

/**
 * Un medio de pago de la venta de mostrador.
 *
 * NO lleva `moneda`, igual que `PagoCobro`: la moneda la define la cuenta a la
 * que entra la plata. Un pago en dolares contra una factura en pesos es normal
 * en el local, y la conversion la hace la base con `cotizacion`.
 */
export interface PagoVentaContado {
  cuenta_id: number;
  importe: number;
  medio: MedioPago;
  cotizacion?: number;
  tarjeta_marca?: string;
  cuotas?: number;
  arancel_pct?: number;
  /** Cuando acredita la tarjeta. La plata todavia no esta disponible. */
  fecha_acreditacion?: string;
  /**
   * Obligatorio con medio CHEQUE o ECHEQ: con esto el cheque entra a cartera
   * (0121_9b). Antes no viajaba y el Banco sumaba un papel que no se podia
   * depositar ni rechazar.
   */
  cheque?: {
    numero: string;
    banco: string;
    librador: string;
    fecha_cobro: string;
    tipo?: "CHEQUE" | "ECHEQ";
  };
}

/**
 * La venta de mostrador: comprobante, stock, caja e imputacion en una sola
 * transaccion. `cliente_id` es opcional porque la mayoria de las ventas del
 * local son a alguien que entra, paga y se va sin ficha.
 */
export interface NuevaVentaContado extends Omit<NuevaVenta, "cliente_id"> {
  cliente_id?: number;
  pagos: PagoVentaContado[];
  /** Equipo usado que el cliente entrega como parte de pago. */
  entrega?: EntregaUsado;
  /** El cliente no quiso el vuelto: queda en la caja como propina. Ver 0085. */
  vuelto_propina?: boolean;
  /**
   * Cuanto del saldo a favor del cliente paga esta venta, en la moneda de la
   * venta. Lo elige quien vende; sin el campo no se usa (0121_8).
   */
  saldo_a_favor?: number;
  /**
   * La llave de idempotencia del ticket. Si la misma venta llega dos veces —un
   * corte de red y el vendedor que aprieta de nuevo— el servidor emite UNA y
   * devuelve el mismo comprobante. Ver 0071.
   */
  clave?: string;
}

/**
 * Un equipo que entra como parte de pago. No es plata: el backend lo da de
 * alta como producto usado con su costo de toma y cuenta el valor como
 * cobrado (con cliente, deja su propio asiento en la cuenta corriente).
 */
export interface EntregaUsado {
  nombre: string;
  /**
   * Si se eligio un iPhone del lineup. Decide la solapa —«Usados» o
   * «Celulares»— en el servidor; antes la decidia el nombre (0109).
   */
  es_iphone?: boolean;
  /** La marca de un telefono que no es iPhone, para su columna. */
  marca?: string;
  valor_toma: number;
  moneda?: Moneda;
  imei?: string;
  bateria_pct?: number;
  color?: string;
  memoria_gb?: number;
  caja_cable?: boolean;
  garantia_meses?: number;
  precio_venta?: number;
  observaciones?: string;
}

/** Cada movimiento de caja que genero la venta, uno por medio de pago. */
export interface MovimientoVentaContado {
  movimiento_id: number;
  cuenta: string;
  medio: MedioPago;
  importe: Numerico;
  moneda: Moneda;
}

/**
 * Lo que devuelve `emitir_venta_contado`.
 *
 * `diferencia` es el campo que mira el vendedor mientras cuenta los billetes:
 * positiva es lo que falta cobrar, negativa es el vuelto que tiene que dar.
 */
export interface ResultadoVentaContado {
  comprobante_id: number;
  numero: string;
  total: Numerico;
  moneda: Moneda;
  cotizacion: Numerico;
  total_ars: Numerico;
  total_usd: Numerico;
  pagado: Numerico;
  /** Lo que quedo debiendo ESTE comprobante, ya imputado. */
  pendiente?: Numerico;
  /** Positivo = falta cobrar. Negativo = hay que dar vuelto. */
  diferencia: Numerico;
  condicion: string | null;
  faltantes?: Array<{ producto_id: number; nombre?: string; faltante: Numerico }>;
  movimientos?: MovimientoVentaContado[];
  /** El equipo recibido en parte de pago, si lo hubo. */
  entrega?: { producto_id: number; nombre: string; valor_toma: Numerico; moneda: Moneda } | null;
  /** Lo que pago el saldo a favor del cliente (0121_8). */
  saldo_a_favor_usado?: Numerico;
}

export interface NuevaNotaCredito {
  /** Sin `items` se acredita la venta completa. */
  items?: Array<{ detalle_id: number; cantidad?: number }>;
  motivo?: string;
  fecha?: string;
  /**
   * De que caja sale la plata. Solo cuenta cuando la venta original no tiene
   * cliente: con cliente el credito va a la cuenta corriente.
   */
  cuenta_id?: number;
}

/** Lo que devuelve emitir una nota de credito. */
export interface ResultadoNotaCredito {
  comprobante_id: number;
  numero: string;
  total: Numerico;
  moneda: Moneda;
  /** La venta acreditada. */
  origen: string;
  cliente_id: number | null;
  /** La plata que salio de la caja, solo en ventas sin cliente. */
  devuelto: {
    cuenta_id: number;
    cuenta: string;
    importe: Numerico;
    moneda: Moneda;
    medio: MedioPago;
  } | null;
}

export interface NuevaNotaDebito {
  cliente_id: number;
  importe: number;
  moneda: Moneda;
  concepto: string;
  cotizacion?: number;
  fecha?: string;
  origen_id?: number;
  observaciones?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Clientes y cuenta corriente
// ─────────────────────────────────────────────────────────────────────────────

/** FIJA congela cada movimiento al dolar de su dia; USD ancla la deuda al dolar. */
export type ModoCuentaCorriente = "FIJA" | "USD";

export interface Cliente {
  id: number;
  codigo: string | null;
  nombre: string;
  documento: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  localidad: string | null;
  cc_modo: ModoCuentaCorriente;
  cc_moneda: Moneda | null;
  limite_credito: Numerico | null;
  limite_moneda: Moneda | null;
  observaciones: string | null;
  activo: boolean;
}

export interface SaldoCliente {
  cliente_id: number;
  nombre: string;
  cc_modo: ModoCuentaCorriente;
  saldo: Numerico;
  saldo_ars: Numerico;
  saldo_usd: Numerico;
  moneda_anclaje: Moneda;
}

export interface MoraCliente {
  cliente_id: number;
  nombre: string;
  saldo: Numerico;
  dias_mora: number;
  antiguedad_deuda: number;
  limite_credito: Numerico | null;
  limite_moneda: Moneda | null;
  excedido: boolean;
}

/** `GET /clientes/:id`: ficha, saldo y mora en una sola llamada. */
export interface FichaCliente {
  cliente: Cliente;
  saldo: SaldoCliente | null;
  mora: MoraCliente | null;
  /**
   * El saldo a favor que puede usar en una venta, en la moneda de su cuenta
   * (0121_8). Opcional: un backend sin la vista no lo manda.
   */
  saldo_a_favor?: Numerico | null;
}

/** Fila del extracto. Solo se lee en orden ascendente: `saldo_acumulado` acumula. */
export interface MovimientoExtracto {
  id: number;
  cliente_id: number;
  fecha: string;
  tipo: string;
  detalle: string | null;
  comprobante_id: number | null;
  debe: Numerico;
  haber: Numerico;
  saldo_acumulado: Numerico;
  moneda: Moneda;
}

export interface FiltrosClientes extends Paginacion {
  q?: string;
  activo?: boolean;
}

export interface NuevoCliente {
  nombre: string;
  codigo?: string;
  documento?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  localidad?: string;
  cc_modo?: ModoCuentaCorriente;
  /** Obligatoria si `cc_modo` es FIJA: es la moneda en la que se cuenta la deuda. */
  cc_moneda?: Moneda;
  limite_credito?: number | null;
  /** Obligatoria si va `limite_credito`. "Limite 1000" sin moneda no dice nada. */
  limite_moneda?: Moneda;
  observaciones?: string;
  activo?: boolean;
}

export type CambiosCliente = Partial<NuevoCliente>;

/**
 * Una fila de `GET /deudas`, que lee la vista `v_alertas_mora`.
 *
 * El tipo anterior declaraba `telefono` y `dias_mora`, que la vista NO devuelve,
 * y se perdia todo lo que si devuelve: los dos saldos, el estado de mora y si
 * excedio el limite. Verificado contra la respuesta real del backend.
 *
 * DOS SALDOS, LOS DOS LEGITIMOS:
 *   · `saldo` esta en la moneda de anclaje del cliente (`moneda_anclaje`).
 *   · `saldo_ars_hoy` es lo que hay que cobrarle HOY en pesos. Para una cuenta
 *     atada al dolar, este sube cuando sube el dolar; el otro no se mueve.
 */
/** Recortes de `GET /deudas`. Ver el comentario de la ruta. */
export interface FiltrosDeudas extends Paginacion {
  /** Nombre, codigo, documento, telefono o email. Lo resuelve la base. */
  buscar?: string;
  /** "1": todos los clientes activos, tengan saldo o no. */
  todos?: "1";
  /** "1": solo los que tienen saldo distinto de cero, esten en mora o no. */
  con_saldo?: "1";
}

export interface FilaDeuda {
  cliente_id: number;
  nombre: string;
  cc_modo: "FIJA" | "USD";
  moneda_anclaje: Moneda;
  saldo: Numerico;
  saldo_ars_hoy: Numerico;
  limite_credito: Numerico | null;
  limite_moneda: Moneda;
  ultimo_cobro: string | null;
  comprobante_mas_viejo: string | null;
  comprobantes_impagos: Numerico;
  dias_sin_cobro: number | null;
  antiguedad_deuda: number | null;
  estado_mora: "AL_DIA" | "MORA_30" | "MORA_60" | "MORA_90";
  excedido: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cobros
// ─────────────────────────────────────────────────────────────────────────────

export type MedioPago =
  | "EFECTIVO_ARS"
  | "EFECTIVO_USD"
  | "TRANSFERENCIA"
  | "DEBITO"
  | "CREDITO"
  | "QR_MP"
  | "CHEQUE"
  | "ECHEQ"
  | "CRIPTO"
  | "OTRO";

export interface DatosCheque {
  numero: string;
  banco?: string;
  fecha_cobro: string;
  titular?: string;
  cuit_titular?: string;
  observaciones?: string;
}

/**
 * Un medio de pago del cobro.
 *
 * NO lleva `moneda`: la define la cuenta. Mandarla es un 400 explicito del
 * backend, porque una moneda que contradice a la caja no tiene resolucion buena.
 */
export interface PagoCobro {
  cuenta_id: number;
  importe: number;
  medio: MedioPago;
  cotizacion?: number;
  /** Obligatorio con medio CHEQUE o ECHEQ: si no, el cheque no entra a cartera. */
  cheque?: DatosCheque;
  observaciones?: string;
}

export interface NuevoCobro {
  /** Llave de idempotencia del cobro. Ver 0071. */
  clave?: string;
  cliente_id: number;
  pagos: PagoCobro[];
  fecha?: string;
  /** La cotizacion de la pantalla (0155): con ella la base mide deuda y vuelto. */
  cotizacion?: number;
  /** Lo pagado de mas es vuelto; con true lo deja de propina (0155). */
  vuelto_propina?: boolean;
  detalle?: string;
  observaciones?: string;
}

export interface NuevoAjusteCuenta {
  /** El signo va en el tipo, no en el importe: un importe negativo se rechaza. */
  tipo?: "AJUSTE+" | "AJUSTE-" | "SALDO_INICIAL" | "ND";
  importe: number;
  moneda: Moneda;
  cotizacion?: number;
  fecha?: string;
  detalle?: string;
  observaciones?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Caja
// ─────────────────────────────────────────────────────────────────────────────

export interface Caja {
  id: number;
  nombre: string;
  tipo: string;
  /** La caja define la moneda. Ningun movimiento la contradice. */
  moneda: Moneda;
  saldo_inicial: Numerico;
  ingresos: Numerico;
  egresos: Numerico;
  saldo: Numerico;
  saldo_ars: Numerico;
  saldo_usd: Numerico;
  movimientos: Numerico;
}

export interface TotalesCaja {
  total_ars: Numerico;
  total_usd: Numerico;
  total_consolidado_ars: Numerico;
  total_consolidado_usd: Numerico;
  cotizacion: Numerico;
}

/**
 * Un dia del resultado de caja (`v_resultado_caja`).
 *
 * Las transferencias entre cajas propias no cuentan: mover plata de la caja al
 * banco no es un ingreso, y sumarlo inflaria el resultado del dia.
 */
/**
 * Una fila de `v_resultado_negocio`: las dos ganancias del dia, separadas.
 *
 * El financiero NO es repetible: sale de la brecha cambiaria del dia. Por eso
 * viaja aparte del operativo y no sumado.
 */
export interface FilaResultadoNegocio {
  dia: string;
  ventas_ars: Numerico;
  costo_ars: Numerico;
  margen_operativo_ars: Numerico;
  arbitraje_caja_ars: Numerico;
  arbitraje_compras_ars: Numerico;
  resultado_financiero_ars: Numerico;
  /**
   * margen + financiero - gastos + diferencias de caja. Los retiros NO restan
   * (0065).
   */
  resultado_total_ars: Numerico;
  gastos_ars: Numerico;
  retiros_ars: Numerico;
  /**
   * Faltantes y sobrantes de arqueo y propinas, con signo (0121_3). Ya estan
   * sumados en `resultado_total_ars`. Opcional: una base sin 0121_3 no la trae.
   */
  diferencias_caja_ars?: Numerico;
  /** Intereses y «Otro» ingreso (0155). Ya estan sumados en `resultado_total_ars`. */
  otros_ingresos_ars?: Numerico;
}

/**
 * Una fila de `v_ventas_costo_estimado`: unidades vendidas sin stock que
 * esperan la compra. Su costo es el de la ficha y su ganancia, estimada (0106).
 */
export interface FilaVentaCostoEstimado {
  id: number;
  comprobante_id: number;
  comprobante_tipo: string;
  numero: string;
  fecha: string;
  producto_id: number;
  codigo_interno: string | null;
  nombre: string;
  cantidad: Numerico;
  costo_estimado: Numerico | null;
  moneda_estimada: Moneda | null;
  /**
   * De donde sale (0125): vendido sin stock que espera la compra, o vendido a
   * pedido que todavia no llego. Los ids son de tablas distintas. SIN_COSTO
   * (0121_3): una linea de venta que salio con costo 0.
   */
  origen?: "SIN_STOCK" | "A_PEDIDO" | "SIN_COSTO";
}

export interface FilaResultadoCaja {
  dia: string;
  ingresos_ars: Numerico;
  egresos_ars: Numerico;
  resultado_ars: Numerico;
  resultado_usd: Numerico;
  movimientos: Numerico;
}

export type ConceptoCaja =
  | "COBRO"
  | "VENTA_CONTADO"
  | "PAGO_PROVEEDOR"
  | "GASTO"
  | "SUELDO"
  | "RETIRO"
  | "APORTE"
  | "TRANSFERENCIA"
  | "CAMBIO_DIVISA"
  | "DEPOSITO_CHEQUE"
  | "IMPUESTO"
  | "DEVOLUCION"
  | "VUELTO"
  | "AJUSTE_ARQUEO"
  | "PROPINA"
  | "ALQUILER"
  | "SERVICIOS"
  | "INSUMOS"
  | "VIATICOS"
  | "MANTENIMIENTO"
  | "PRESTAMO"
  | "RECHAZO_CHEQUE"
  | "COMISION"
  | "DEVOLUCION_PRESTAMO"
  | "INTERESES"
  | "OTRO";

export interface MovimientoCaja {
  id: number;
  fecha: string;
  cuenta_id: number;
  tipo: "INGRESO" | "EGRESO";
  concepto: ConceptoCaja;
  medio: MedioPago | null;
  importe: Numerico;
  moneda: Moneda;
  cotizacion: Numerico | null;
  cliente_id: number | null;
  proveedor_id: number | null;
  /** De que socio es un retiro, un aporte o un sueldo de socio (0089, 0108). */
  socio_id?: number | null;
  /** La venta que cobro este movimiento. Lo usa el detalle de `GET /ventas/:id`. */
  comprobante_id: number | null;
  /** Solo con tarjeta: la marca, las cuotas y el arancel que descuenta. */
  tarjeta_marca: string | null;
  cuotas: number | null;
  arancel_pct: Numerico | null;
  detalle: string | null;
  observaciones: string | null;
  /** Quien lo cargo (0155). */
  usuario_id?: string | null;
  usuario_nombre?: string | null;
  orden_id?: number | null;
  movimiento_cc_id?: number | null;
  movimiento_cp_id?: number | null;
  transferencia_id?: number | null;
  /** En el espejo de una anulacion, el movimiento que anula (0155). */
  anula_id?: number | null;
  /** Cuando se anulo este movimiento (0155). */
  anulado_at?: string | null;
  /** La revision cruzada (0156): cuando, quien y por que. */
  revisado_at?: string | null;
  revisado_por?: string | null;
  revision_nota?: string | null;
}

export interface FiltrosMovimientosCaja extends Paginacion {
  /** Texto libre: detalle, observaciones, concepto o importe. */
  q?: string;
  cuenta_id?: number;
  tipo?: "INGRESO" | "EGRESO";
  concepto?: ConceptoCaja;
  desde?: string;
  hasta?: string;
}

/** Lo que entro y lo que salio de UNA moneda, con cuantos movimientos son. */
export interface TotalMovimientos {
  entro: Numerico;
  salio: Numerico;
  cantidad: number;
}

/**
 * El pie de la lista de movimientos.
 *
 * No hay ningun campo que sume las dos monedas. Las TRANSFERENCIAS entre
 * cuentas propias no estan en los importes y se cuentan aparte: son un egreso y
 * un ingreso por el mismo importe, y sumarlas infla las dos columnas.
 */
export interface TotalesMovimientos {
  ars: TotalMovimientos;
  usd: TotalMovimientos;
  transferencias: number;
}

export interface PaginaMovimientosCaja extends Pagina<MovimientoCaja> {
  totales?: TotalesMovimientos;
}

/**
 * Un cambio de divisa con su resultado (`v_resultado_cambios`).
 *
 * `diferencia_ars` es lo que el cambio dejo o costo contra la cotizacion que
 * tenia el sistema ese dia. Negativa no significa error: significa que se pago
 * mas caro que el oficial, que a veces es la unica forma de conseguir billetes.
 */
export interface FilaCambioDivisa {
  id: number;
  fecha: string;
  origen: string;
  destino: string;
  importe_origen: Numerico;
  moneda_origen: Moneda;
  importe_destino: Numerico;
  moneda_destino: Moneda;
  cotizacion_operacion: Numerico | null;
  cotizacion_sistema: Numerico | null;
  diferencia_ars: Numerico;
}

/** Una venta con tarjeta y lo que de verdad llega (`v_liquidaciones_tarjeta`). */
export interface FilaLiquidacionTarjeta {
  id: number;
  fecha: string;
  tarjeta_marca: string | null;
  cuotas: number | null;
  bruto: Numerico;
  moneda: Moneda;
  arancel_pct: Numerico | null;
  neto: Numerico | null;
  arancel: Numerico;
  fecha_acreditacion: string | null;
  acreditado: boolean;
  cliente: string | null;
  cuenta: string;
}

export interface FiltrosTarjetas {
  desde?: string;
  hasta?: string;
  /** Sin el filtro vienen las dos: las que ya se acreditaron y las que no. */
  acreditado?: boolean;
}

export interface PaginaTarjetas extends Pagina<FilaLiquidacionTarjeta> {
  /** El NETO por moneda, que es la plata que llega. No el bruto. */
  totales?: { ars: TotalMoneda; usd: TotalMoneda };
}

/** Movimiento suelto: un gasto, un retiro, un aporte. Sin `moneda`: la pone la cuenta. */
export interface NuevoMovimientoCaja {
  cuenta_id: number;
  tipo: "INGRESO" | "EGRESO";
  importe: number;
  concepto?: ConceptoCaja;
  medio?: MedioPago;
  fecha?: string;
  cotizacion?: number;
  cliente_id?: number;
  proveedor_id?: number;
  /** Obligatorio con RETIRO y APORTE, prohibido en el resto (0089). */
  socio_id?: number;
  detalle?: string;
  observaciones?: string;
}

/** Lo que se manda al contar una caja. Sin `esperado`: lo calcula la base (0069). */
export interface NuevoArqueo {
  cuenta_id: number;
  contado: number;
  fecha?: string;
  cotizacion?: number;
  observaciones?: string;
}

/** Lo que devuelve el arqueo, una vez guardado. */
export interface ResultadoArqueo {
  arqueo_id: number;
  cuenta: string;
  moneda: Moneda;
  contado: Numerico;
  esperado: Numerico;
  diferencia: Numerico;
  movimiento_id: number | null;
  saldo_ahora: Numerico;
}

export interface NuevaTransferencia {
  origen_id: number;
  destino_id: number;
  importe: number;
  /** La cotizacion REAL del cambio: es la que decide si hubo ganancia o perdida. */
  cotizacion?: number;
  /** Si el cambio se pacto en un numero redondo, se informa el destino directo. */
  importe_destino?: number;
  medio?: MedioPago;
  fecha?: string;
  motivo?: string;
}

export interface CuentaFinanciera {
  id: number;
  nombre: string;
  tipo: string;
  moneda: Moneda;
  orden: number;
  activo: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cheques
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoCheque =
  "EN_CARTERA" | "DEPOSITADO" | "ACREDITADO" | "RECHAZADO" | "ENTREGADO" | "ANULADO" | string;

export interface Cheque {
  id: number;
  numero: string;
  banco: string | null;
  importe: Numerico;
  moneda: Moneda;
  fecha_cobro: string;
  estado: EstadoCheque;
  cliente_id: number | null;
  titular: string | null;
  observaciones: string | null;
  /** El movimiento del cobro con que entro. Null si se cargo a mano. */
  movimiento_id?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Taller
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoOt =
  | "RECIBIDO"
  | "DIAGNOSTICO"
  | "PRESUPUESTADO"
  | "APROBADO"
  | "EN_REPARACION"
  | "LISTO"
  | "ENTREGADO"
  | "SIN_ARREGLO"
  /** El cliente no quiso pagar el presupuesto. No es lo mismo que no tener arreglo. */
  | "RECHAZADO"
  | "ANULADO";

/** Por que volvio el equipo. Sobrevive a ENTREGADO, que es cuando el estado ya no los distingue. */
export type MotivoCierreOt = "SIN_ARREGLO" | "RECHAZADO";

export interface OrdenReparacion {
  id: number;
  numero: string | null;
  fecha_ingreso: string;
  cliente_id: number | null;
  cliente: string | null;
  equipo: string;
  modelo: string | null;
  imei: string | null;
  color: string | null;
  falla_declarada: string;
  estado: EstadoOt;
  mano_obra_importe: Numerico | null;
  mano_obra_moneda: Moneda | null;
  garantia_dias: number | null;
  observaciones: string | null;
}

export interface FiltrosReparaciones {
  estado?: EstadoOt;
  cliente_id?: number;
  limite?: number;
}

export interface TableroTaller {
  tablero: Array<{ estado: EstadoOt; ordenes: Numerico; [k: string]: unknown }>;
  sin_retirar: OrdenReparacion[];
}

/** `cliente_id` es opcional a proposito: el equipo entra antes que la ficha. */
export interface NuevaOrdenReparacion {
  equipo: string;
  falla_declarada: string;
  cliente_id?: number;
  /**
   * Orden interna: el usado del stock que se manda a reacondicionar. Con esto
   * no va cliente ni diagnostico; la base lo rechaza si vienen juntos.
   */
  producto_id?: number;
  modelo?: string;
  imei?: string;
  color?: string;
  password_equipo?: string;
  /** Apple ID o cuenta de Google. Sensible: la base la borra al entregar. */
  cuenta_equipo?: string;
  /** La clave del equipo cuando es un numero. Es una cosa o la otra con el patron. */
  pin_equipo?: string;
  /** Patron de desbloqueo como "1-2-3-6": puntos de la grilla 3x3, del 1 al 9. */
  patron_equipo?: string;
  accesorios?: string;
  /** Con que encomienda viaja el equipo al servicio tecnico. */
  transporte?: string;
  /** A que servicio tecnico externo se manda. */
  tercerizado?: string;
  mano_obra_importe?: number;
  mano_obra_moneda?: Moneda;
  garantia_dias?: number;
  observaciones?: string;
  /** El cobro del diagnostico, en el mismo acto que el alta. */
  diagnostico?: CobroDiagnostico;
}

/**
 * El cobro que habilita la mesa de trabajo.
 *
 * Con `motivo` NO se cobra y queda dicho por que. El cobro va por defecto, pero
 * no puede impedir que el equipo entre al sistema: un equipo en el taller sin
 * orden es peor que un equipo sin cobrar.
 */
export interface CobroDiagnostico {
  importe: number;
  moneda?: Moneda;
  motivo?: string;
  cuenta_id?: number;
  medio?: MedioPago;
  /** Lo que entra a la caja, en la moneda de la caja. Sin esto lo convierte la base. */
  importe_cobrado?: number;
  cotizacion?: number;
}

/** Lo que sale hacia el tecnico tercerizado. Sin importe, lo que dice el presupuesto. */
export interface PagoTecnicoOt {
  cuenta_id: number;
  medio: MedioPago;
  importe?: number;
  cotizacion?: number;
  detalle?: string;
}

/** Lo que devuelve `POST /reparaciones`: la orden nueva y, si se cobro, el cobro. */
export interface ResultadoRecepcion {
  orden_id: number;
  numero: string;
  diagnostico_importe: Numerico;
  diagnostico_moneda: Moneda;
  diagnostico_motivo: string | null;
  cobro: ResultadoSenaOt | null;
}

export interface ResultadoPagoTecnicoOt {
  movimiento_id: number;
  orden_id: number;
  numero: string;
  importe: Numerico;
  moneda: Moneda;
  cuenta: string;
}

/**
 * La liquidacion con el tecnico: varias ordenes y varias cajas de una vez.
 *
 * `moneda_deuda` dice a que bloque va cada pago. Sin eso, un pago en pesos
 * seria ambiguo cuando se deben pesos Y dolares.
 */
export interface PagoTecnicoLote {
  ordenes: number[];
  pagos: Array<{
    cuenta_id: number;
    medio: MedioPago;
    importe: number;
    moneda_deuda?: Moneda;
  }>;
  cotizacion?: number;
  detalle?: string;
}

export interface ResultadoPagoTecnicoLote {
  ordenes: number;
  movimientos: number;
  cotizacion: Numerico;
  /** Lo que salio de cada moneda de caja: `{ "USD": 687, "ARS": 24323 }`. */
  total: Partial<Record<Moneda, Numerico>>;
}

/** Lo que ya se cargo alguna vez, del mas usado al menos. Se arma solo con el uso. */
export interface SugerenciasOt {
  equipos: string[];
  transportes: string[];
  tercerizados: string[];
  /** Lo de la ultima orden, para precargar: casi siempre es el mismo tecnico. */
  ultimo: { transporte: string | null; tercerizado: string | null };
}

/** Una fila de `v_usados_en_taller`: un usado del stock con orden interna abierta. */
export interface UsadoEnTaller {
  producto_id: number;
  orden_id: number;
  numero: string;
  estado: EstadoOt;
  fecha_ingreso: string;
}

/**
 * Los accesos del equipo, que NO viajan en la lista de ordenes: se piden aparte
 * por `GET /reparaciones/:id/accesos`, de a uno y cuando hacen falta. Despues
 * de entregar el equipo los tres vienen en `null`.
 */
export interface AccesosEquipo {
  cuenta_equipo: string | null;
  password_equipo: string | null;
  pin_equipo: string | null;
  patron_equipo: string | null;
  estado: EstadoOt;
}

/**
 * Los tres montos de una reparacion tercerizada. El precio al cliente no se
 * manda: la base lo calcula como la suma.
 */
export interface CostosOt {
  /** Queda en cero: la encomienda ya no forma parte del precio. */
  encomienda?: number;
  costo_tecnico: number;
  ganancia: number;
  moneda?: Moneda;
  diagnostico?: string;
}

export interface ResultadoCostosOt {
  orden_id: number;
  numero: string;
  estado: EstadoOt;
  encomienda: Numerico;
  costo_tecnico: Numerico;
  ganancia: Numerico;
  precio_cliente: Numerico;
  moneda: Moneda;
  /** El cliente ya habia aprobado: el precio no se movio y la diferencia salio de la ganancia. */
  precio_congelado?: boolean;
}

/** Plata que deja el cliente al recibir el equipo. La caja decide la moneda. */
export interface SenaOt {
  cuenta_id: number;
  medio: MedioPago;
  importe: number;
  cotizacion?: number;
  /** Con medio CHEQUE o ECHEQ: con esto el cheque entra a cartera (0155). */
  cheque?: PagoVentaContado["cheque"];
}

/**
 * Devolver lo cobrado de una orden anulada (0121_9). El importe va en la
 * moneda de la caja; sin importe la base devuelve todo lo que queda.
 */
export interface DevolverSenaOt {
  cuenta_id: number;
  medio?: MedioPago;
  importe?: number;
}

export interface ResultadoSenaOt {
  movimiento_id: number;
  orden_id: number;
  numero: string;
  importe: Numerico;
  moneda: Moneda;
  cuenta: string;
}

/** Entregar: factura, cobra lo de hoy, descuenta las señas y cierra la orden. */
export interface EntregaOt {
  pagos?: PagoVentaContado[];
  cotizacion?: number;
  /** Por que vuelve el equipo. Sin esto, se entrega reparado. */
  cierre?: MotivoCierreOt;
  /** Y en ese caso, si se le cobra igual el diagnostico. Por defecto si. */
  cobrar_diagnostico?: boolean;
  /** Solo en una orden interna: el precio de venta nuevo del usado. Sin esto no se toca. */
  precio_venta?: number;
  /**
   * Cuanto del saldo a favor del cliente paga la entrega, en la moneda de la
   * factura (0121_9f). Nunca se aplica solo.
   */
  saldo_a_favor?: number;
  /** Lo pagado de mas es vuelto en pesos; con true el cliente lo deja de propina (0155). */
  vuelto_propina?: boolean;
}

export interface ResultadoEntregaOt {
  orden_id: number;
  numero: string;
  estado: EstadoOt;
  precio: Numerico;
  moneda: Moneda;
  senas: Numerico;
  comprobante_id: number | null;
  comprobante: string | null;
  pagado: Numerico | null;
  pendiente: Numerico | null;
  /** Por que volvio, si volvio sin arreglar. */
  cierre?: MotivoCierreOt | null;
  /** Los pesos que salieron de vuelto (sin cliente, cobrada de mas; 0121_9f). */
  vuelto?: Numerico | null;
  // ── Solo en una orden interna (0048) ──
  producto_id?: number | null;
  costo_anterior?: Numerico | null;
  costo_nuevo?: Numerico | null;
  costo_moneda?: Moneda | null;
  precio_venta?: Numerico | null;
  /** El usado ya se habia vendido cuando volvio: el costo queda en el producto pero no entro en esa venta. */
  ya_vendido?: boolean | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuracion
// ─────────────────────────────────────────────────────────────────────────────

export interface EmpresaConfig {
  id: number;
  nombre: string;
  razon_social: string | null;
  cuit: string | null;
  direccion: string | null;
  localidad: string | null;
  telefono: string | null;
  whatsapp: string | null;
  instagram: string | null;
  email: string | null;
  logo_url: string | null;
  cotizacion_modo: "AUTO" | "MANUAL";
  cotizacion_manual: Numerico | null;
  spread_pct: Numerico;
  /** Modelos del lineup que el local no trabaja: "16 PRO MAX", "16E". */
  modelos_ocultos: string[] | null;
  /** Memorias que el local no trabaja, de un modelo que si: "17 PRO MAX|512". */
  memorias_ocultas: string[] | null;
  ajuste_fijo: Numerico;
  dias_mora_alerta: number;
  umbral_match_nombre: Numerico;
  /** La regla del taller: lo que se precarga como encomienda y como ganancia, en dolares. */
  reparacion_encomienda_usd?: Numerico;
  reparacion_ganancia_usd?: Numerico;
  /** Lo que se cobra al recibir un equipo, en dolares. */
  reparacion_diagnostico_usd?: Numerico;
  /** Que porcentaje del presupuesto se pide para arrancar, contando lo ya pago. */
  reparacion_anticipo_pct?: Numerico;
  /** El titulo y la bajada del home de la tienda (0093). `null` es el de siempre. */
  titulo_home?: string | null;
  bajada_home?: string | null;
  /** El orden y el titulo propio de las secciones del home (0095). */
  secciones_home?: { clave: string; titulo: string | null; visible?: boolean }[] | null;
  /** El nombre de cada tarjeta en la tienda, por clave (0095). */
  nombres_tienda?: Record<string, string> | null;
  /** Lo que promete el home de la tienda sobre los usados. */
  garantia_usado_meses?: number | null;
  /** Cuando se cerro la puesta a punto (0146). Null: todavia esta abierta. */
  arranque_at?: string | null;
}

export interface RespuestaConfig {
  config: EmpresaConfig | null;
  /** La del mostrador: el blue mas el ajuste fijo. Con la que se vende. */
  cotizacion_vigente: number;
  /**
   * La misma sin el ajuste fijo. Es a la que se le paga al tecnico: el ajuste
   * es el margen de venta del local y no tiene que ir adentro de un egreso.
   */
  cotizacion_tecnico?: number;
  /**
   * Cuantos productos activos reprecia mover la cotizacion: los de costo y
   * precio en monedas distintas y sin precio a mano. Solo en GET /config.
   */
  repreciables?: number;
}

/**
 * Cambios de configuracion.
 *
 * `cotizacion_manual` y `cotizacion_modo` no son un campo mas: moverlos dispara
 * `trg_empresa_config_reprecia`, que recalcula el precio de venta de TODO el
 * catalogo. Ver la invalidacion en `queries.ts`.
 */
export type CambiosConfig = Partial<{
  nombre: string;
  razon_social: string;
  cuit: string;
  direccion: string;
  localidad: string;
  telefono: string;
  whatsapp: string;
  instagram: string;
  email: string;
  logo_url: string;
  /** Vacio vuelve al texto de siempre. */
  titulo_home: string | null;
  bajada_home: string | null;
  secciones_home: { clave: string; titulo: string | null; visible?: boolean }[] | null;
  nombres_tienda: Record<string, string> | null;
  cotizacion_modo: "AUTO" | "MANUAL";
  cotizacion_manual: number;
  spread_pct: number;
  modelos_ocultos: string[];
  memorias_ocultas: string[];
  ajuste_fijo: number;
  umbral_match_nombre: number;
}>;

// ─────────────────────────────────────────────────────────────────────────────
// Socios
// ─────────────────────────────────────────────────────────────────────────────

/** Lo que mas se carga a mano en Caja (0156). */
export interface FrecuenteCaja {
  detalle: string;
  concepto: string;
  tipo: "INGRESO" | "EGRESO";
  veces: number;
}

/** Un renglon de los gastos del periodo por categoria (0156). */
export interface GastoPorCategoria {
  concepto: string;
  total_ars: Numerico;
  cantidad: number;
}

/** Un dueño del local. Los retiros y aportes van a su nombre (0089). */
export interface Socio {
  id: number;
  nombre: string;
  activo: boolean;
  /** Lo que cobra por mes (0156). Sin esto no se carga sueldo de socio. */
  sueldo_importe?: Numerico | null;
  sueldo_moneda?: Moneda | null;
}

/** SUELDO no entra en el neto: es un gasto del local, no plata que se reparte (0108). */
export type ClaseMovimientoSocio = "RETIRO_PLATA" | "RETIRO_MERCADERIA" | "APORTE" | "SUELDO";

/**
 * Una fila de `v_movimientos_socios`. Los importes van SIEMPRE positivos: el
 * signo lo da `clase` (un aporte resta del neto, los retiros suman).
 */
export interface MovimientoSocio {
  clave: string;
  fecha: string;
  /** null: retiro o aporte cargado antes de 0089, todavia sin dueño. */
  socio_id: number | null;
  socio: string | null;
  clase: ClaseMovimientoSocio;
  importe_usd: Numerico;
  importe_ars: Numerico;
  /** El importe en su moneda original: la de la caja, o USD en la mercaderia. */
  importe: Numerico;
  moneda: Moneda;
  cuenta: string | null;
  detalle: string | null;
  origen: "CAJA" | "STOCK";
  origen_id: Numerico;
}

export interface PaginaMovimientosSocios extends Pagina<MovimientoSocio> {
  /** Los retiros y aportes sin socio, de cualquier fecha. */
  sin_asignar: MovimientoSocio[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Encargos (0111): lo vendido a pedido, hasta que llega y se entrega
// ─────────────────────────────────────────────────────────────────────────────

export type EstadoEncargo = "POR_RECIBIR" | "POR_ENTREGAR" | "ENTREGADO";

export interface Encargo {
  id: number;
  comprobante_id: number;
  venta: string;
  fecha_venta: string;
  cliente_id: number | null;
  cliente: string | null;
  producto_id: number;
  producto: string;
  color: string | null;
  memoria_gb: number | null;
  es_usado: boolean;
  bateria_pct: number | null;
  proveedor_id: number | null;
  proveedor: string | null;
  costo_estimado: Numerico | null;
  moneda_estimada: Moneda | null;
  costo_real: Numerico | null;
  moneda_real: Moneda | null;
  recibido_at: string | null;
  entregado_at: string | null;
  compra_id: string | null;
  estado: EstadoEncargo;
  /** El color o la variante que pidio el cliente, anotado en el mostrador (0114). */
  nota?: string | null;
  /**
   * El proveedor de la compra que lo trajo (0121_4). Null si se recibio a
   * mano. Puede no ser `proveedor`, que es el anotado al vender.
   */
  proveedor_compra?: string | null;
  /**
   * Lo que todavia se debe de la venta del encargo, en su moneda (0121_9a).
   * Para avisar al entregarlo.
   */
  venta_pendiente?: Numerico | null;
  venta_moneda?: Moneda | null;
}
